import { Redirect, Switch } from 'react-router'

import { ProtectedRoute } from '@webapp/app'
import { useAppRoutes } from '@webapp/hooks'
import { type FeatureSwitches } from '@webapp/models'
import { type PermissionEnum, Permissions } from '@webapp/platform/iam'
import { CreateCreditCardBillPage } from './CreateCreditCardBillPage'
import { CreditCardAccountDetailsPage } from './CreditCardAccountDetailsPage'
import { CreditCardReconciliationPage } from './CreditCardReconciliationPage'
import { ReconciliationMatchPage } from './ReconciliationMatchPage'
import { AccountDetailsPageTabs, MatchPageTabs } from './types'

// The Angular `ap` parent state redirects to `/` when AP navigation is
// disabled — mirror that guard on every reconcile route. `customFilter` is
// required because AND-of-permission+switch cannot be expressed with the
// ANY-of `permissions` prop.
const reconcileRouteFilter = ({
  featureSwitches,
  permissions,
}: {
  featureSwitches: FeatureSwitches
  permissions: PermissionEnum[]
}) =>
  !featureSwitches.disable_ap_navigation &&
  permissions.includes(Permissions.ACCOUNTS_PAYABLE_RECONCILIATION)

export const ReconcileRouter = () => {
  const { brp } = useAppRoutes()

  return (
    <Switch>
      <ProtectedRoute
        exact
        path={brp('CreditCardReconciliationPage')}
        component={CreditCardReconciliationPage}
        customFilter={reconcileRouteFilter}
      />

      {/* The literal `create` segment must win over the match tab param */}
      <ProtectedRoute
        exact
        path={brp('CreateCreditCardBillPage', { id: ':id' })}
        component={CreateCreditCardBillPage}
        customFilter={reconcileRouteFilter}
      />

      <ProtectedRoute
        exact
        path={brp('ReconciliationMatchPage', {
          id: ':id',
          tab: ':tab(unmatched|matched)',
        })}
        component={ReconciliationMatchPage}
        customFilter={reconcileRouteFilter}
      />

      {/* Handles the tab-less `/match` path AND unknown match tabs */}
      <Redirect
        from={brp('ReconciliationMatchPage', { id: ':id', tab: ':tab?' })}
        to={brp('ReconciliationMatchPage', {
          id: ':id',
          tab: MatchPageTabs.UNMATCHED,
        })}
      />

      <ProtectedRoute
        exact
        path={brp('CreditCardAccountDetailsPage', {
          id: ':id',
          tab: ':tab(active|archived)',
        })}
        component={CreditCardAccountDetailsPage}
        customFilter={reconcileRouteFilter}
      />

      {/* Handles the tab-less `/:id` path AND unknown details tabs */}
      <Redirect
        from={brp('CreditCardAccountDetailsPage', { id: ':id', tab: ':tab?' })}
        to={brp('CreditCardAccountDetailsPage', {
          id: ':id',
          tab: AccountDetailsPageTabs.ACTIVE,
        })}
      />
    </Switch>
  )
}
